import { graphql } from 'react-relay';
import { useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import uuid from 'uuid/v4';
import unionBy from 'lodash/unionBy';
import type { MutationUpdaterFn } from 'apollo-client';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import debounce from 'lodash/debounce';

import { createFilter } from '@atlaskit/select';
import type { GroupedOptionsType } from '@atlaskit/select';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	LABELS_SEARCH_LABEL_EXPERIENCE,
	LABELS_RECOMMENDED_EXPERIENCE,
	LABELS_ADD_LABEL_EXPERIENCE,
	LABELS_CREATE_LABEL_EXPERIENCE,
	LABELS_DELETE_LABEL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { isViewPage, isBlogPage } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import { parseErrorMessage } from '../helpers.js';
import { ALLOWED_CONTENT_STATUSES, ActionTypes, INVALID_CHARACTERS } from '../constants.tsx';

import {
	type useLabelsSelectPropsQuery$data,
	type useLabelsSelectPropsQuery$variables,
} from './__generated__/useLabelsSelectPropsQuery.graphql.ts';
import type {
	useLabelsSelectPropsRecommendedLabelsQuery$data,
	useLabelsSelectPropsRecommendedLabelsQuery$variables,
} from './__generated__/useLabelsSelectPropsRecommendedLabelsQuery.graphql.ts';
import type {
	useLabelsSelectPropsLabelSearchQuery$data,
	useLabelsSelectPropsLabelSearchQuery$variables,
} from './__generated__/useLabelsSelectPropsLabelSearchQuery.graphql.ts';
import type {
	useLabelsSelectPropsDeleteLabelMutation$data,
	useLabelsSelectPropsDeleteLabelMutation$variables,
} from './__generated__/useLabelsSelectPropsDeleteLabelMutation.graphql.ts';
import type {
	useLabelsSelectPropsAddLabelsMutation$data,
	useLabelsSelectPropsAddLabelsMutation$variables,
} from './__generated__/useLabelsSelectPropsAddLabelsMutation.graphql.ts';
import type {
	useLabelsSelectPropsSSRLabelsQuery$data,
	useLabelsSelectPropsSSRLabelsQuery$variables,
} from './__generated__/useLabelsSelectPropsSSRLabelsQuery.graphql.ts';
import type { CustomOptionType } from './LabelsSelect.tsx';

const useLabelsSelectPropsQuery = graphql`
	query useLabelsSelectPropsQuery($contentId: ID!, $refetchQuery: Boolean!, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				type
				operations @skip(if: $refetchQuery) {
					operation
					targetType
				}
				space {
					id
				}
				labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
					count
					nodes {
						__typename
						id
						label
						name
						prefix
					}
				}
			}
		}
	}
`;

const useLabelsSelectPropsSSRLabelsQuery = graphql`
	query useLabelsSelectPropsSSRLabelsQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				type
				status
				operations {
					operation
					targetType
				}
				space {
					id
				}
				labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
					count
					nodes {
						__typename
						id
						label
						name
						prefix
					}
				}
			}
		}
	}
`;
const useLabelsSelectPropsAddLabelsMutation = graphql`
	mutation useLabelsSelectPropsAddLabelsMutation($contentId: ID!, $labels: [LabelInput!]!) {
		addLabels(input: { contentId: $contentId, labels: $labels }) {
			__typename
			labels {
				__typename
				count
				nodes {
					__typename
					id
					prefix
					name
					label
				}
			}
		}
	}
`;
const useLabelsSelectPropsDeleteLabelMutation = graphql`
	mutation useLabelsSelectPropsDeleteLabelMutation($contentId: ID!, $label: String!) {
		deleteLabel(input: { contentId: $contentId, label: $label }) {
			contentId
			label
		}
	}
`;
const useLabelsSelectPropsLabelSearchQuery = graphql`
	query useLabelsSelectPropsLabelSearchQuery($contentId: ID!, $searchText: String!) {
		contentLabelSearch(contentId: $contentId, searchText: $searchText) {
			suggestedLabels {
				prefix
				name
				id
				label
			}
			otherLabels {
				prefix
				name
				id
				label
			}
		}
	}
`;
const useLabelsSelectPropsRecommendedLabelsQuery = graphql`
	query useLabelsSelectPropsRecommendedLabelsQuery($contentId: ID!, $spaceId: ID!) {
		getRecommendedLabels(entityId: $contentId, entityType: "page", spaceId: $spaceId) {
			recommendedLabels {
				id
				name
				namespace
				strategy
			}
		}
	}
`;
const i18n = defineMessages({
	suggestedLabels: {
		id: 'labels.use-labels-select-props.suggested-labels',
		defaultMessage: 'Suggested labels',
		description:
			'heading for options in labels select that are recommended by smarts label service',
	},
});

const getValidationError = (inputValue: string) => {
	const parsedInputValue = inputValue.replace(/^my:/, '');
	if (parsedInputValue.match(INVALID_CHARACTERS)) {
		// list taken from monolith here: com/atlassian/confluence/labels/LabelParser.java:39
		return 'labelsValidationInvalidCharacter';
	} else if (parsedInputValue.match(/^~/)) {
		return 'labelsValidationTilde';
	}
	if (parsedInputValue.length > 255) {
		return 'labelsValidationLength';
	}
	return '';
};

const isLabelNotFoundError = (error) =>
	/No label found with name/.test(parseErrorMessage(error).message);

const isContentNotFoundError = (error) =>
	/No content found with id/.test(parseErrorMessage(error).message);

const isLabelDeletePermissionsError = (error) =>
	/Could not delete label from content with id/.test(parseErrorMessage(error).message);

const isLabelMutationLoggedOutError = (error) =>
	/Current user not permitted to use Confluence/.test(parseErrorMessage(error).message);

const isLabelSearch403Error = (error) =>
	/Unexpected status code 403/.test(parseErrorMessage(error).message);

export const getPrivacySafePrefix = (prefix = '') => prefix.match(/my|global|team/)?.[0];

export const isStarredLabel: (label: string) => boolean = (label) =>
	label === 'my:favourite' || label === 'my:favorite';

const filterByPrefix = (labels, prefix) =>
	(labels || []).filter((label) => label?.prefix === prefix);

const transformToPrefixedLabel = (prefix, label) =>
	`${prefix && prefix !== 'global' ? `${prefix}:` : ''}${label}`;

const sanitizeInputValue = (input: string) => {
	return input.toLowerCase().replace(/\s/g, '-');
};

const getRecommendedLabelsFromData = (data) => data?.getRecommendedLabels?.recommendedLabels;

const makeTransformLabelToOption = (strategy) => (label) => {
	const prefixedLabel = transformToPrefixedLabel(label.prefix, label.label);
	return {
		id: label.id,
		label: prefixedLabel,
		value: prefixedLabel,
		prefix: label.prefix,
		name: label.name,
		strategy,
	};
};

const transformRecommendedLabelToOption = (label) => {
	const prefixedLabel = transformToPrefixedLabel(label.namespace, label.name);
	return {
		id: label.id,
		label: prefixedLabel,
		value: prefixedLabel,
		prefix: label.namespace,
		name: label.name,
		strategy: 'recommended',
		subStrategy: label.strategy,
	};
};

const transformCurrentLabelsToSelectedOptions = (currentLabels): CustomOptionType[] => {
	if (!currentLabels) {
		return [];
	}
	return (currentLabels?.content?.nodes?.[0]?.labels?.nodes || [])
		.map(makeTransformLabelToOption('current'))
		.filter(({ label }) => !isStarredLabel(label));
};

const useOptions = ({ searchLabelQueryResults, recommendedLabelsQueryResults, hasInput }) => {
	const intl = useIntl();
	return useMemo(() => {
		if (hasInput) {
			const otherLabelOptions = (
				searchLabelQueryResults?.contentLabelSearch?.otherLabels || []
			).map(makeTransformLabelToOption('searched_other'));

			const suggestedLabelOptions = (
				searchLabelQueryResults?.contentLabelSearch?.suggestedLabels || []
			).map(makeTransformLabelToOption('searched_suggested'));
			return [...suggestedLabelOptions, ...otherLabelOptions];
		} else {
			const recommendedLabelOptions = (
				getRecommendedLabelsFromData(recommendedLabelsQueryResults) || []
			).map(transformRecommendedLabelToOption);
			return [
				{
					label: intl.formatMessage(i18n.suggestedLabels),
					options: recommendedLabelOptions,
				},
			];
		}
	}, [searchLabelQueryResults, recommendedLabelsQueryResults, hasInput, intl]);
};

type LabelsSelectPropsType = ({
	contentId,
	source,
	onUpdateSuccess,
	onAdded,
}: {
	contentId: string;
	source: string;
	onUpdateSuccess?: () => void;
	onAdded?: () => void;
}) => {
	options: GroupedOptionsType<CustomOptionType>;
	filterOption: (option, inputValue: string) => boolean;
	value: CustomOptionType[];
	onChange: (label: any, action: any) => any;
	onMenuOpen: () => void;
	onInputChange: (inputValue: string) => void;
	inputValue: string;
	isLoading: boolean;
	isDisabled?: boolean;
	labelsQueryLoading: boolean;
	labelsQueryError?: ApolloErrorInterop;
	labelsMutationError?: ApolloErrorInterop;
	validationError: string;
	labelsSearchQueryError?: ApolloErrorInterop;
	fetchRecommendedLabels: () => void;
};

export const useLabelsSelectProps: LabelsSelectPropsType = ({
	contentId,
	source,
	onUpdateSuccess,
	onAdded,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { activationId } = useSessionData();
	const [validationError, setValidationError] = useState('');
	const [inputValue, setInputValue] = useState('');
	const isLabelsPreloadedFromContent = Boolean(
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		(window.__SSR_RENDERED__ && (isViewPage() || isBlogPage())) || process.env.REACT_SSR,
	);

	const {
		data: ssrLabelsQueryData,
		loading: ssrLabelsQueryLoading,
		error: ssrLabelsQueryError,
	} = useQueryInterop<
		useLabelsSelectPropsSSRLabelsQuery$data,
		useLabelsSelectPropsSSRLabelsQuery$variables
	>(useLabelsSelectPropsSSRLabelsQuery, {
		variables: {
			contentId,
		},
		skip: !isLabelsPreloadedFromContent,
	});
	const {
		data: labelsQueryData,
		loading: labelsQueryLoading,
		error: labelsQueryError,
	} = useQueryInterop<useLabelsSelectPropsQuery$data, useLabelsSelectPropsQuery$variables>(
		useLabelsSelectPropsQuery,
		{
			variables: {
				contentId,
				refetchQuery: false,
				status: ALLOWED_CONTENT_STATUSES,
			},
			skip: isLabelsPreloadedFromContent,
			fetchPolicy: 'cache-and-network',
		},
	);

	const labelData = useMemo(() => {
		if (isLabelsPreloadedFromContent) {
			if (!ALLOWED_CONTENT_STATUSES.includes(ssrLabelsQueryData?.content?.nodes?.[0]?.status || ''))
				return null;
			return ssrLabelsQueryData;
		}
		return labelsQueryData;
	}, [ssrLabelsQueryData, labelsQueryData, isLabelsPreloadedFromContent]);

	const selectedOptions = useMemo(
		() => transformCurrentLabelsToSelectedOptions(labelData),
		[labelData],
	);

	const type = labelData?.content?.nodes?.[0]?.type;
	const spaceId = labelData?.content?.nodes?.[0]?.space?.id || '';
	// this will be used to disable add/delete if the user doesn't have page edit permissions
	const isDisabled = !labelData?.content?.nodes?.[0]?.operations?.find((item) => {
		return item?.operation === 'update' && item?.targetType === type;
	});

	useEffect(() => {
		!(labelsQueryLoading || ssrLabelsQueryLoading) &&
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'labels',
					containerId: spaceId,
					containerType: 'space',
					objectId: contentId,
					objectType: type,
					source,
					attributes: {
						count: {
							global: filterByPrefix(selectedOptions, 'global').length,
							personal: filterByPrefix(selectedOptions, 'my').length,
							total: selectedOptions.length,
						},
						...(isLivePage ? { isLivePage } : null),
					},
				},
			}).fire();
	}, [
		labelsQueryLoading,
		ssrLabelsQueryLoading,
		selectedOptions,
		spaceId,
		contentId,
		type,
		source,
		createAnalyticsEvent,
		isLivePage,
	]);
	const [addLabel, { error: addError }] = useMutationInterop<
		useLabelsSelectPropsAddLabelsMutation$data,
		useLabelsSelectPropsAddLabelsMutation$variables
	>(useLabelsSelectPropsAddLabelsMutation, {
		update(_, { data }) {
			const _cachedData = readQueryInterop<
				useLabelsSelectPropsQuery$data,
				useLabelsSelectPropsQuery$variables
			>({
				query: useLabelsSelectPropsQuery,
				variables: {
					contentId,
					refetchQuery: false,
					status: ALLOWED_CONTENT_STATUSES,
				},
			});
			const cachedData = JSON.parse(JSON.stringify(_cachedData)); // converting read-only to mutable
			if (cachedData?.content?.nodes?.[0] && data?.addLabels?.labels) {
				const newLabels = unionBy(
					cachedData.content.nodes[0].labels?.nodes,
					data.addLabels?.labels.nodes,
					'id',
				);
				cachedData.content.nodes[0].labels = {
					count: newLabels.length,
					nodes: newLabels,
					// @ts-ignore
					__typename: 'PaginatedLabelList',
				};
				writeQueryInterop({
					query: useLabelsSelectPropsQuery,
					variables: {
						contentId,
						refetchQuery: false,
						status: ALLOWED_CONTENT_STATUSES,
					},
					data: cachedData,
				});
			}
		},
		onCompleted: () => {
			if (onUpdateSuccess) {
				onUpdateSuccess();
			}
			if (onAdded) {
				onAdded();
			}
		},
	});
	const handleDeleteLabelUpdate: MutationUpdaterFn<useLabelsSelectPropsDeleteLabelMutation$data> =
		useCallback(
			(_, { data }) => {
				const cachedData = readQueryInterop<
					useLabelsSelectPropsQuery$data,
					useLabelsSelectPropsQuery$variables
				>({
					query: useLabelsSelectPropsQuery,
					variables: {
						contentId,
						refetchQuery: false,
						status: ALLOWED_CONTENT_STATUSES,
					},
				});

				if (
					cachedData?.content?.nodes?.[0]?.labels?.nodes &&
					cachedData?.content?.nodes?.[0]?.labels?.count != null
				) {
					// TODO: label is not a unique identifier. Either prefix + label or label id should be used instead, but neither are returned by the mutation
					const filteredCachedLabels = cachedData.content.nodes[0].labels.nodes.filter(
						(node) => node?.label !== data?.deleteLabel?.label,
					);
					writeQueryInterop<useLabelsSelectPropsQuery$data, useLabelsSelectPropsQuery$variables>({
						query: useLabelsSelectPropsQuery,
						variables: {
							contentId,
							refetchQuery: false,
							status: ALLOWED_CONTENT_STATUSES,
						},
						data: {
							content: {
								nodes: [
									{
										...cachedData.content.nodes[0],
										labels: {
											nodes: filteredCachedLabels,
											count: filteredCachedLabels.length,
											// @ts-ignore
											__typename: 'PaginatedLabelList',
										},
									},
								],
								__typename: 'PaginatedContentListWithChild',
							},
						},
					});
				}
			},
			[contentId],
		);

	const [deleteLabel, { error: rawDeleteError }] = useMutationInterop<
		useLabelsSelectPropsDeleteLabelMutation$data,
		useLabelsSelectPropsDeleteLabelMutation$variables
	>(useLabelsSelectPropsDeleteLabelMutation, {
		update: handleDeleteLabelUpdate,
		onCompleted: () => {
			if (onUpdateSuccess) {
				onUpdateSuccess();
			}
		},
	});
	const deleteError = !isLabelNotFoundError(rawDeleteError) ? rawDeleteError : undefined;

	const [
		searchQuery,
		{
			data: searchLabelQueryResults,
			loading: searchLabelQueryLoading,
			error: searchLabelQueryError,
		},
	] = useLazyQueryInterop<
		useLabelsSelectPropsLabelSearchQuery$data,
		useLabelsSelectPropsLabelSearchQuery$variables
	>(useLabelsSelectPropsLabelSearchQuery, {
		onCompleted: () => {
			experienceTracker.succeed({
				name: LABELS_SEARCH_LABEL_EXPERIENCE,
			});
		},
		onError: (error) => {
			if (isLabelSearch403Error(error)) {
				markErrorAsHandled(error);
				experienceTracker.abort({
					name: LABELS_SEARCH_LABEL_EXPERIENCE,
					reason: 'User was logged out',
				});
			}
			experienceTracker.fail({
				error,
				name: LABELS_SEARCH_LABEL_EXPERIENCE,
			});
		},
	});
	const [
		recommendedLabelsQuery,
		{ data: recommendedLabelsQueryResults, loading: recommendedLabelsQueryLoading },
	] = useLazyQueryInterop<
		useLabelsSelectPropsRecommendedLabelsQuery$data,
		useLabelsSelectPropsRecommendedLabelsQuery$variables
	>(useLabelsSelectPropsRecommendedLabelsQuery, {
		variables: {
			contentId,
			spaceId,
		},
		onCompleted: (data) => {
			experienceTracker.succeed({
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
			const recommendedLabels = getRecommendedLabelsFromData(data);
			recommendedLabels &&
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'shown',
						actionSubject: 'labelRecommendations',
						source,
						containerId: spaceId,
						containerType: 'space',
						objectId: contentId,
						objectType: type,
						attributes: {
							count: recommendedLabels?.length,
						},
					},
				}).fire();
		},
		onError: (error) => {
			experienceTracker.fail({
				error,
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
		},
	});
	const fetchRecommendedLabels = useCallback(() => {
		if (type === 'page' && !isDisabled) {
			experienceTracker.start({
				name: LABELS_RECOMMENDED_EXPERIENCE,
			});
			recommendedLabelsQuery();
		}
	}, [type, experienceTracker, recommendedLabelsQuery, isDisabled]);

	const recommendedLabels = getRecommendedLabelsFromData(recommendedLabelsQueryResults);
	const options = useOptions({
		searchLabelQueryResults,
		recommendedLabelsQueryResults,
		hasInput: Boolean(inputValue),
	});

	const debouncedSearchQuery = useMemo(
		() => debounce(searchQuery, 500, { leading: true }),
		[searchQuery],
	);

	const handleInputChange = (inputValue) => {
		const error = getValidationError(inputValue);
		setValidationError(error);
		setInputValue(sanitizeInputValue(inputValue));
		if (Boolean(inputValue)) {
			experienceTracker.start({ name: LABELS_SEARCH_LABEL_EXPERIENCE });
			if (!validationError) {
				debouncedSearchQuery({
					variables: {
						searchText: inputValue,
						contentId,
					},
				});
			}
		} else {
			fetchRecommendedLabels();
		}
	};

	const filterOption = ({ value }) => {
		return (
			!searchLabelQueryError &&
			createFilter({}) &&
			!isStarredLabel(value) &&
			!getValidationError(value)
		);
	};

	const fireLabelChangeEvent = useCallback(
		({ action, labels }: any) => {
			labels.map(({ id: labelId, name: labelName, prefix: labelPrefix, strategy, subStrategy }) => {
				const recommendedIndex = recommendedLabels?.findIndex(({ id }) => id === labelId);
				let recommendedRanking;
				if (recommendedIndex >= 0) {
					recommendedRanking = recommendedIndex;
					if (strategy?.startsWith('searched')) {
						strategy = 'recommended_searched';
						subStrategy = recommendedLabels[recommendedIndex].strategy;
					}
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action,
						actionSubject: 'label',
						source,
						objectType: type,
						objectId: contentId,
						containerType: 'space',
						containerId: spaceId,
						attributes: {
							contentId,
							labelId,
							strategy,
							subStrategy,
							isNewLabel: strategy === 'newly_created',
							privacySafeLabelPrefix: getPrivacySafePrefix(labelPrefix),
							activationId,
							recommendedCount: recommendedLabels?.length,
							hasRecommendations: !!recommendedLabels?.length,
							recommendedRanking,
							...(isLivePage ? { isLivePage } : null),
						},
						nonPrivacySafeAttributes: {
							labelName,
							labelPrefix,
						},
					},
				}).fire();
			});
		},
		[
			type,
			source,
			contentId,
			spaceId,
			createAnalyticsEvent,
			activationId,
			recommendedLabels,
			isLivePage,
		],
	);

	const handleSelectLabel = useCallback(
		async (option: any) => {
			experienceTracker.start({
				name: LABELS_ADD_LABEL_EXPERIENCE,
			});
			try {
				setInputValue('');

				// const optimisticResponse = J

				await addLabel({
					variables: {
						labels: [{ name: option.name, prefix: option.prefix }],
						contentId,
					},
					optimisticResponse: {
						addLabels: {
							__typename: 'AddLabelsPayload',
							labels: {
								__typename: 'PaginatedLabelList',
								count: (labelData?.content?.nodes?.[0]?.labels?.count || 0) + 1,
								nodes: [
									...(labelData?.content?.nodes?.[0]?.labels?.nodes || []),
									{
										__typename: 'Label',
										id: option.id,
										prefix: option.prefix,
										name: option.name,
										label: option.name,
									},
								],
							},
						},
					},
				});
				experienceTracker.succeed({ name: LABELS_ADD_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'add',
					labels: [option],
				});
			} catch (error) {
				experienceTracker.fail({
					error,
					name: LABELS_ADD_LABEL_EXPERIENCE,
				});
			}
		},
		[experienceTracker, addLabel, labelData, fireLabelChangeEvent, contentId],
	);

	const handleCreateLabel = useCallback(
		async (option: any) => {
			experienceTracker.start({
				name: LABELS_CREATE_LABEL_EXPERIENCE,
			});
			try {
				setInputValue('');
				const { data } = await addLabel({
					variables: {
						labels: [{ name: option.value, prefix: '' }],
						contentId,
					},
					optimisticResponse: {
						addLabels: {
							__typename: 'AddLabelsPayload',
							labels: {
								__typename: 'PaginatedLabelList',
								count: (labelData?.content?.nodes?.[0]?.labels?.count || 0) + 1,
								nodes: [
									...(labelData?.content?.nodes?.[0]?.labels?.nodes || []),
									{
										name: option.value,
										prefix: '',
										__typename: 'Label',
										id: uuid(),
										label: option.value,
									},
								],
							},
						},
					},
				});
				experienceTracker.succeed({ name: LABELS_CREATE_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'add',
					// filter out the newly added labels from the already existing - this is necessary in order to get the new label ids to send in the analytics event
					labels: (data?.addLabels?.labels?.nodes || [])
						.map(makeTransformLabelToOption('newly_created'))
						.filter(({ value }) => option.value === value),
				});
			} catch (error) {
				experienceTracker.fail({
					error,
					name: LABELS_CREATE_LABEL_EXPERIENCE,
				});
			}
		},
		[experienceTracker, addLabel, labelData, fireLabelChangeEvent, contentId],
	);

	const handleDeleteLabel = useCallback(
		async (removedValue: any) => {
			const optimisticResponse = {
				deleteLabel: {
					__typename: 'DeleteLabelPayload',
					contentId,
					label: removedValue?.name,
				},
			};

			if (!removedValue) return;
			experienceTracker.start({
				name: LABELS_DELETE_LABEL_EXPERIENCE,
			});
			try {
				await deleteLabel({
					variables: {
						label: removedValue.name,
						contentId,
					},
				});
				experienceTracker.succeed({ name: LABELS_DELETE_LABEL_EXPERIENCE });
				fireLabelChangeEvent({
					action: 'deleted',
					labels: [removedValue],
				});
			} catch (error) {
				if (isLabelNotFoundError(error)) {
					markErrorAsHandled(error);
					handleDeleteLabelUpdate(getApolloClient(), {
						data: optimisticResponse,
					});
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'label already deleted',
					});
				} else if (isContentNotFoundError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'Content was deleted',
					});
				} else if (isLabelDeletePermissionsError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'Edit permissions were removed',
					});
				} else if (isLabelMutationLoggedOutError(error)) {
					markErrorAsHandled(error);
					experienceTracker.abort({
						name: LABELS_DELETE_LABEL_EXPERIENCE,
						reason: 'User was logged out',
					});
				} else {
					experienceTracker.fail({
						error,
						name: LABELS_DELETE_LABEL_EXPERIENCE,
					});
				}
			}
		},
		[experienceTracker, deleteLabel, fireLabelChangeEvent, contentId, handleDeleteLabelUpdate],
	);

	const handleLabelChange = async (_, { action, option, removedValue }) => {
		if (action === ActionTypes.SELECT_OPTION) {
			void handleSelectLabel(option);
		} else if (action === ActionTypes.CREATE_OPTION) {
			void handleCreateLabel(option);
		} else if (action === ActionTypes.REMOVE_VALUE || action === ActionTypes.POP_VALUE) {
			void handleDeleteLabel(removedValue);
		}
	};

	const handleMenuOpen = useCallback(() => {
		fetchRecommendedLabels();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'open',
				actionSubject: 'labelsSelect',
				source,
				containerId: spaceId,
				containerType: 'space',
				objectId: contentId,
				objectType: type,
			},
		}).fire();
	}, [source, spaceId, contentId, type, createAnalyticsEvent, fetchRecommendedLabels]);

	const isLoading = searchLabelQueryLoading || recommendedLabelsQueryLoading;

	return {
		options: isLoading ? [] : options,
		value: !(labelsQueryLoading || ssrLabelsQueryLoading) ? selectedOptions : [],
		filterOption,
		onChange: handleLabelChange,
		onMenuOpen: handleMenuOpen,
		onInputChange: handleInputChange,
		inputValue,
		isLoading,
		isDisabled,
		labelsQueryLoading: labelsQueryLoading || ssrLabelsQueryLoading,
		labelsQueryError: labelsQueryError || ssrLabelsQueryError,
		labelsMutationError: addError || deleteError,
		labelsSearchQueryError: searchLabelQueryError,
		validationError,
		fetchRecommendedLabels,
	};
};
